import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { 
  Briefcase, 
  Copy, 
  Check,
  Clock,
  ArrowLeft,
  FileText,
  MessageSquare,
  Target,
  Star,
  Save,
  UserPlus,
  History,
  Sparkles,
  Lock
} from "lucide-react";

// Language-specific labels
const LABELS = {
  en: {
    pageTitle: "Your Interview Preparation",
    pageSubtitle: "Personalized content tailored to your CV and the",
    scripts: "Scripts",
    keyAnswers: "Key Answers",
    qa: "Q&A",
    star: "STAR",
    twoMinIntro: "2-Minute Introduction",
    fiveMinWalkthrough: "5-Minute Experience Walkthrough",
    strengths: "Your Strengths",
    weaknesses: "Your Weaknesses",
    likelyQuestions: "Likely Interview Questions",
    questionsForEmployer: "Questions for the Employer",
    starStories: "STAR Method Stories",
    copyAnswer: "Copy Answer",
    copyStory: "Copy Story",
    copied: "Copied",
    savePrep: "Save This Interview Prep",
    saveDescription: "Create a free account to keep this preparation and access it anytime",
    accessHistory: "Access your prep history",
    saveMultiple: "Save multiple preps",
    refineAI: "Refine with AI chat",
    createAccount: "Create Free Account",
    readyToAce: "Ready to ace your interview?",
    saveAndCreate: "Save this prep and create more personalized preparations",
    newPrep: "New Prep",
    saveBtn: "Save Prep",
    signUpFree: "Sign Up Free",
    situation: "Situation",
    task: "Task",
    action: "Action",
    result: "Result"
  },
  fr: {
    pageTitle: "Votre Préparation d'Entretien",
    pageSubtitle: "Contenu personnalisé adapté à votre CV et au poste de",
    scripts: "Scripts",
    keyAnswers: "Réponses Clés",
    qa: "Q&R",
    star: "STAR",
    twoMinIntro: "Introduction de 2 Minutes",
    fiveMinWalkthrough: "Parcours d'Expérience de 5 Minutes",
    strengths: "Vos Points Forts",
    weaknesses: "Vos Points à Améliorer",
    likelyQuestions: "Questions d'Entretien Probables",
    questionsForEmployer: "Questions pour l'Employeur",
    starStories: "Exemples Méthode STAR",
    copyAnswer: "Copier la Réponse",
    copyStory: "Copier l'Exemple",
    copied: "Copié",
    savePrep: "Sauvegarder Cette Préparation",
    saveDescription: "Créez un compte gratuit pour conserver cette préparation et y accéder à tout moment",
    accessHistory: "Accédez à votre historique",
    saveMultiple: "Sauvegardez plusieurs préparations",
    refineAI: "Affinez avec le chat IA",
    createAccount: "Créer un Compte Gratuit",
    readyToAce: "Prêt à réussir votre entretien ?",
    saveAndCreate: "Sauvegardez cette préparation et créez-en d'autres personnalisées",
    newPrep: "Nouvelle Préparation",
    saveBtn: "Sauvegarder",
    signUpFree: "S'inscrire Gratuitement",
    situation: "Situation",
    task: "Tâche",
    action: "Action",
    result: "Résultat"
  }
};

const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [copiedSection, setCopiedSection] = useState(null);
  const [activeTab, setActiveTab] = useState("scripts");
  const [showSaveBanner, setShowSaveBanner] = useState(true);

  const { interviewData, jobData, cvData, interviewType, isAnonymous } = location.state || {};
  
  // Detect language from the response or default to English
  const detectedLang = interviewData?.detected_language || "en";
  const labels = LABELS[detectedLang] || LABELS.en;

  useEffect(() => {
    if (!interviewData) {
      navigate("/");
    }
  }, [interviewData, navigate]);

  if (!interviewData) {
    return null;
  }

  const content = interviewData.content || interviewData;

  const copyToClipboard = async (text, sectionId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(sectionId);
      toast.success(detectedLang === "fr" ? "Copié !" : "Copied to clipboard!");
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      toast.error(detectedLang === "fr" ? "Échec de la copie" : "Failed to copy");
    }
  };

  // Handle save/signup click
  const handleSaveClick = () => {
    // Store the generated prep data for retrieval after auth
    const prepData = {
      interviewData,
      jobData,
      cvData,
      interviewType,
      timestamp: Date.now()
    };
    sessionStorage.setItem('hireun_generated_prep', JSON.stringify(prepData));
    
    // Navigate to login with message
    const message = detectedLang === "fr" 
      ? "Créez un compte pour sauvegarder cette préparation d'entretien"
      : "Create an account to save this interview prep and access it anytime";
    navigate("/login", {
      state: {
        from: { pathname: "/app" },
        message,
        hasPendingPrep: true,
        prepToSave: true
      }
    });
  };

  const CopyButton = ({ text, sectionId }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => copyToClipboard(text, sectionId)}
      className="copy-btn"
      data-testid={`copy-${sectionId}`}
    >
      {copiedSection === sectionId ? (
        <Check className="w-4 h-4 text-green-500" />
      ) : (
        <Copy className="w-4 h-4" />
      )}
    </Button>
  );

  const ContentSection = ({ title, content: sectionContent, icon: Icon, badge, sectionId }) => (
    <div className="content-section" data-testid={`section-${sectionId}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-accent rounded-lg">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
            {badge && (
              <Badge variant="secondary" className="mt-1">{badge}</Badge>
            )}
          </div>
        </div>
        <CopyButton text={sectionContent} sectionId={sectionId} />
      </div>
      <div className="prose prose-sm max-w-none">
        <p className="text-foreground whitespace-pre-wrap leading-relaxed">{sectionContent}</p>
      </div>
    </div>
  );

  // Save CTA Banner Component
  const SaveCTABanner = ({ variant = "full" }) => {
    if (!isAnonymous) return null;
    
    if (variant === "sticky") {
      return (
        <div className="fixed bottom-0 left-0 right-0 bg-primary text-white p-4 shadow-lg z-50 md:hidden">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Save className="w-5 h-5" />
              <span className="text-sm font-medium">{labels.savePrep}</span>
            </div>
            <Button 
              onClick={handleSaveClick}
              variant="secondary"
              size="sm"
              data-testid="save-cta-sticky"
            >
              {labels.signUpFree}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 rounded-xl">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {labels.savePrep}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-3">
                    {labels.saveDescription}
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <History className="w-4 h-4 text-primary" />
                      <span>{labels.accessHistory}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Save className="w-4 h-4 text-primary" />
                      <span>{labels.saveMultiple}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MessageSquare className="w-4 h-4 text-primary" />
                      <span>{labels.refineAI}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 w-full md:w-auto">
                <Button 
                  onClick={handleSaveClick}
                  className="flex-1 md:flex-none bg-primary hover:bg-primary/90"
                  size="lg"
                  data-testid="save-cta-main"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  {labels.createAccount}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  // Inline Save CTA (appears between sections)
  const InlineSaveCTA = () => {
    if (!isAnonymous) return null;
    
    return (
      <div className="my-8 p-4 border border-dashed border-primary/30 rounded-lg bg-primary/5 text-center">
        <Lock className="w-5 h-5 text-primary mx-auto mb-2" />
        <p className="text-sm text-muted-foreground mb-3">
          <span className="font-medium text-foreground">Want to save this and create more preps?</span>
        </p>
        <Button 
          onClick={handleSaveClick}
          variant="outline"
          size="sm"
          data-testid="save-cta-inline"
        >
          Create Free Account
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                New Prep
              </Button>
              <div className="hidden md:block h-6 w-px bg-border" />
              <div className="hidden md:block">
                <p className="font-medium text-sm">{jobData?.title || interviewData?.job_title || "Interview Prep"}</p>
                <p className="text-xs text-muted-foreground">{jobData?.company || interviewData?.company_name || ""}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {isAnonymous && (
                <Button 
                  onClick={handleSaveClick}
                  variant="default"
                  size="sm"
                  className="hidden md:flex"
                  data-testid="save-btn-header"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {labels.saveBtn}
                </Button>
              )}
              <Link to="/" className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-primary" />
                <span className="font-semibold hidden sm:block" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Hireun
                </span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 md:pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Save CTA Banner - Top */}
          <SaveCTABanner variant="full" />

          {/* Page Title */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {labels.pageTitle}
            </h1>
            <p className="text-muted-foreground">
              {labels.pageSubtitle} {jobData?.title || interviewData?.job_title || "position"}
            </p>
          </div>

          {/* Tabs Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-8">
              <TabsTrigger value="scripts" data-testid="tab-scripts">
                <FileText className="w-4 h-4 mr-2 hidden sm:block" />
                {labels.scripts}
              </TabsTrigger>
              <TabsTrigger value="answers" data-testid="tab-answers">
                <MessageSquare className="w-4 h-4 mr-2 hidden sm:block" />
                {labels.keyAnswers}
              </TabsTrigger>
              <TabsTrigger value="questions" data-testid="tab-questions">
                <Target className="w-4 h-4 mr-2 hidden sm:block" />
                {labels.qa}
              </TabsTrigger>
              <TabsTrigger value="behavioral" data-testid="tab-behavioral">
                <Star className="w-4 h-4 mr-2 hidden sm:block" />
                {labels.star}
              </TabsTrigger>
            </TabsList>

            {/* Scripts Tab */}
            <TabsContent value="scripts" className="space-y-6">
              <ContentSection
                title={labels.twoMinIntro}
                content={content.two_minute_script || content.two_min_intro || (detectedLang === "fr" ? "Non disponible" : "Not available")}
                icon={Clock}
                badge={detectedLang === "fr" ? "~250 mots" : "~250 words"}
                sectionId="two-min-intro"
              />
              <ContentSection
                title={labels.fiveMinWalkthrough}
                content={content.five_minute_script || content.five_min_walkthrough || (detectedLang === "fr" ? "Non disponible" : "Not available")}
                icon={FileText}
                badge={detectedLang === "fr" ? "~500 mots" : "~500 words"}
                sectionId="five-min-walkthrough"
              />
              
              <InlineSaveCTA />
            </TabsContent>

            {/* Answers Tab */}
            <TabsContent value="answers" className="space-y-6">
              <ContentSection
                title={labels.strengths}
                content={content.strengths || (detectedLang === "fr" ? "Non disponible" : "Not available")}
                icon={Star}
                sectionId="strengths"
              />
              <ContentSection
                title={labels.weaknesses}
                content={content.weaknesses || (detectedLang === "fr" ? "Non disponible" : "Not available")}
                icon={Target}
                sectionId="weaknesses"
              />
              
              <InlineSaveCTA />
            </TabsContent>

            {/* Q&A Tab */}
            <TabsContent value="questions" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary" />
                    {labels.likelyQuestions}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    {(content.likely_questions || []).map((qa, index) => (
                      <AccordionItem key={index} value={`qa-${index}`}>
                        <AccordionTrigger className="text-left">
                          <span className="flex items-center gap-2">
                            <Badge variant="secondary" className="w-6 h-6 flex items-center justify-center p-0">
                              {index + 1}
                            </Badge>
                            {qa.question || qa}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="pl-8 pr-4">
                            <p className="text-muted-foreground whitespace-pre-wrap">
                              {qa.answer || (detectedLang === "fr" ? "Voir les exemples STAR pour les questions comportementales." : "See STAR stories for behavioral questions.")}
                            </p>
                            {qa.answer && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="mt-2"
                                onClick={() => copyToClipboard(qa.answer, `qa-${index}`)}
                              >
                                {copiedSection === `qa-${index}` ? (
                                  <><Check className="w-4 h-4 mr-1 text-green-500" /> {labels.copied}</>
                                ) : (
                                  <><Copy className="w-4 h-4 mr-1" /> {labels.copyAnswer}</>
                                )}
                              </Button>
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    {labels.questionsForEmployer}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {(content.questions_for_employer || []).map((question, index) => (
                      <li key={index} className="flex items-start gap-3 p-3 bg-secondary/50 rounded-lg">
                        <Badge variant="outline" className="mt-0.5">{index + 1}</Badge>
                        <span>{question}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
              
              <InlineSaveCTA />
            </TabsContent>

            {/* STAR Tab */}
            <TabsContent value="behavioral" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-primary" />
                    {labels.starStories}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    {(content.star_stories || []).map((story, index) => (
                      <AccordionItem key={index} value={`star-${index}`}>
                        <AccordionTrigger className="text-left">
                          <span className="flex items-center gap-2">
                            <Star className="w-4 h-4 text-yellow-500" />
                            {story.title || story.question || story.situation?.substring(0, 50) || (detectedLang === "fr" ? `Exemple ${index + 1}` : `Story ${index + 1}`)}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-4 pl-6">
                            {story.situation && (
                              <div>
                                <Badge className="mb-2">{labels.situation}</Badge>
                                <p className="text-muted-foreground">{story.situation}</p>
                              </div>
                            )}
                            {story.task && (
                              <div>
                                <Badge className="mb-2">{labels.task}</Badge>
                                <p className="text-muted-foreground">{story.task}</p>
                              </div>
                            )}
                            {story.action && (
                              <div>
                                <Badge className="mb-2">{labels.action}</Badge>
                                <p className="text-muted-foreground">{story.action}</p>
                              </div>
                            )}
                            {story.result && (
                              <div>
                                <Badge className="mb-2">{labels.result}</Badge>
                                <p className="text-muted-foreground">{story.result}</p>
                              </div>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => copyToClipboard(
                                `${labels.situation}: ${story.situation}\n${labels.task}: ${story.task}\n${labels.action}: ${story.action}\n${labels.result}: ${story.result}`,
                                `star-${index}`
                              )}
                            >
                              {copiedSection === `star-${index}` ? (
                                <><Check className="w-4 h-4 mr-1 text-green-500" /> {labels.copied}</>
                              ) : (
                                <><Copy className="w-4 h-4 mr-1" /> {labels.copyStory}</>
                              )}
                            </Button>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
              
              <InlineSaveCTA />
            </TabsContent>
          </Tabs>

          {/* Insights CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8"
          >
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Sparkles className="w-5 h-5 text-primary" />
              {detectedLang === 'fr' ? 'Allez plus loin' : 'Go Further'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Quiz CTA */}
              <Card className="hover:border-primary/50 transition-colors cursor-pointer" onClick={() => navigate(`/insights?company=${encodeURIComponent(jobData?.company || '')}&title=${encodeURIComponent(jobData?.title || '')}&tab=quiz`)}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Target className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-sm">
                          {detectedLang === 'fr' ? 'Quiz Entreprise' : 'Company Quiz'}
                        </h4>
                        <Badge variant="secondary" className="text-xs">
                          {detectedLang === 'fr' ? 'Gratuit' : 'Free'}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {detectedLang === 'fr' 
                          ? `Testez vos connaissances sur ${jobData?.company || "l'entreprise"}`
                          : `Test your knowledge about ${jobData?.company || 'the company'}`
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Salary CTA */}
              <Card className="hover:border-primary/50 transition-colors cursor-pointer" onClick={() => navigate(`/insights?company=${encodeURIComponent(jobData?.company || '')}&title=${encodeURIComponent(jobData?.title || '')}&location=${encodeURIComponent(jobData?.location || '')}&tab=salary`)}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-sm">
                          {detectedLang === 'fr' ? 'Benchmark Salaire' : 'Salary Benchmark'}
                        </h4>
                        <Badge variant="outline" className="text-xs">
                          <Lock className="w-3 h-3 mr-1" />
                          Premium
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {detectedLang === 'fr' 
                          ? 'Fourchette de salaire et conseils de négociation'
                          : 'Salary range and negotiation tips'
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Network CTA */}
              <Card className="hover:border-primary/50 transition-colors cursor-pointer" onClick={() => navigate(`/insights?company=${encodeURIComponent(jobData?.company || '')}&title=${encodeURIComponent(jobData?.title || '')}&tab=network`)}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-sm">
                          {detectedLang === 'fr' ? 'Profils Similaires' : 'Similar Profiles'}
                        </h4>
                        <Badge variant="outline" className="text-xs">
                          <Lock className="w-3 h-3 mr-1" />
                          Premium
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {detectedLang === 'fr' 
                          ? 'Réseautez avec des professionnels similaires'
                          : 'Network with similar professionals'
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Bottom Save CTA */}
          {isAnonymous && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-12 text-center"
            >
              <Card className="border-primary/20 bg-primary/5 inline-block">
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {labels.readyToAce}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {labels.saveAndCreate}
                  </p>
                  <Button 
                    onClick={handleSaveClick}
                    size="lg"
                    data-testid="save-cta-bottom"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    {labels.createAccount}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </motion.div>
      </main>

      {/* Sticky Mobile Save CTA */}
      <SaveCTABanner variant="sticky" />
    </div>
  );
};

export default ResultsPage;
